"use client";
"use no memo";

import type {
  ColumnDef,
  PaginationState,
  SortingState,
} from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { Dispatch, SetStateAction } from "react";
import { use } from "react";

import TableLoader from "@/components/shared/table-loader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useFocusMarketData } from "@/contexts/focus-market-data";
import { UserContext } from "@/contexts/user-context";
import { cn } from "@/utils/lib";

interface TrendingCoinsTableProps<T> {
  loading: boolean;
  columns: Array<ColumnDef<T>>;
  data: Array<T>;
  columnSorting: SortingState;
  setColumnSorting: Dispatch<SetStateAction<SortingState>>;
  pagination: PaginationState;
  pageOffset: number;
  columnsWidth: Array<number>;
  wrapperClassName?: string;
  refetchData?: () => void;
  emptyTableClassName?: string;
  noItemsMessage?: string;
}

const TrendingCoinsTable = <T,>({
  data,
  columns,
  loading,
  columnSorting,
  setColumnSorting,
  pagination,
  pageOffset,
  columnsWidth,
  wrapperClassName = "",
  refetchData,
  emptyTableClassName = "",
  noItemsMessage = "No tokens found.",
}: TrendingCoinsTableProps<T>) => {
  const { currentUserPublicKey } = use(UserContext);
  const { focusExchangeRate, appState } = useFocusMarketData();

  // TanStack Table exposes methods React Compiler cannot memoize safely.
  // This table is intentionally opted out of compiler memoization.
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setColumnSorting,
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualSorting: true,
    state: {
      sorting: columnSorting,
      pagination,
    },
  });

  return (
    <div className="w-full rounded-md border text-sm">
      <Table className={cn("min-w-[800px]", wrapperClassName)}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              className={cn(
                "flex w-full rounded-tr-md border-b",
                loading && "pointer-events-none opacity-50",
              )}
            >
              {headerGroup.headers.map((header, idx) => {
                return (
                  <TableHead
                    className={cn(
                      `
                          flex h-11 items-center rounded-tr-md px-2 text-left
                          text-xs font-medium text-muted-foreground
                        `,
                      header.id === "actions"
                        ? "sticky right-0 z-1 bg-background"
                        : "relative z-0",
                    )}
                    style={{ width: `${columnsWidth[idx]}%` }}
                    key={header.id}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          <TableRow className="relative h-0 border-none">
            <TableCell colSpan={table.getHeaderGroups().length} className="p-0">
              {loading && <TableLoader />}
            </TableCell>
          </TableRow>

          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className="flex border-b"
                data-state={row.getIsSelected() && "selected"}
              >
                {row.getVisibleCells().map((cell, idx) => {
                  return (
                    <TableCell
                      key={cell.id}
                      style={{ width: `${columnsWidth[idx]}%` }}
                      className={cn(
                        cell.column.id === "actions"
                          ? "sticky right-0 z-1 bg-card/50 p-0"
                          : "relative z-0",
                        cell.column.id === "circulatingSupply" && "w-[300px]",
                      )}
                    >
                      <div className="flex size-full items-center">
                        {flexRender(cell.column.columnDef.cell, {
                          ...cell.getContext(),
                          focusExchangeRate,
                          desoExchangeRate:
                            appState?.USDCentsPerDeSoExchangeRate ?? 0,
                          currentUserPublicKey,
                          pageOffset,
                          refetchData,
                        })}
                      </div>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className={cn("h-24 text-center", emptyTableClassName)}
              >
                {noItemsMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default TrendingCoinsTable;
